import { Box, debounce } from "@mui/material"
import { useCallback, useEffect, useRef } from "react"
import { refreshToken, selectUser, validateToken } from "../features/user/userSlice"
import { useAppDispatch, useAppSelector } from "./hooks"
import { store } from "./store"
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import { selectActivityTimeout } from "../features/settings/settingsSlice"

const delta = 15 * 60 // if token left timelife is less than delta then issue refresh token request

function max(x: number, y: number): number {
    return x > y ? x : y
}

function poll(f: () => Promise<number | undefined>, delay: number) {
    setTimeout(() => {
        f().then(timeLeft => {
            if (timeLeft !== undefined) {
                const nextDelay = max(Math.floor(timeLeft / 2) * 1000, 1000)
                // console.log("Next token validation [s]:"); console.log(nextDelay / 1000)
                poll(f, nextDelay)
            }
        })
    }, delay)
}

export const SessionManager = () => {
    const dispatch = useAppDispatch()
    const { tokenState } = useAppSelector(selectUser)
    const activityTimeout = useAppSelector(selectActivityTimeout)

    const visibleRef = useRef(true)
    const inactivityRef = useRef(false)

    const validate = useCallback(async () => {
        const state = store.getState()
        const { tokenState } = state.user

        if (tokenState && tokenState.tag === 'valid') {
            try {
                const { timeLeftInSeconds } = await dispatch(validateToken(tokenState.token)).unwrap()
                // console.log("Left in [s] from server:"); console.log(timeLeftInSeconds);
                if (timeLeftInSeconds < delta && inactivityRef.current === false) {
                    await dispatch(refreshToken())
                    // console.log("Token refreshed!")
                    // console.log(inactivityRef.current)
                    return undefined
                } else {
                    return timeLeftInSeconds
                }
            } catch (error) {
                // token invalid
                // console.log("Token expired")
                return undefined
            }
        } else {
            return undefined
        }
    }, [])

    const handleVisibilityChange = useCallback(() => {
        visibleRef.current = !document.hidden
        // console.log("Visibility:")
        // console.log(!document.hidden)
    }, [])

    const inactive = useRef(
        debounce(() => {
            inactivityRef.current = true
            // console.log("Inactivity: TRUE.")
        }, activityTimeout ?? 5 * 60 * 1000)
    )

    const handleMouseActivity = useCallback(() => {
        inactivityRef.current = false
        // console.log("Inactivity: FALSE.")
        inactive.current()
    }, [])

    useEffect(() => {
        document.addEventListener('visibilitychange', handleVisibilityChange)
        document.body.addEventListener('mouseover', handleMouseActivity)

        return  () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange)
            document.body.removeEventListener('mouseover', handleMouseActivity)
        }
    }, [])

    useEffect(() => {
        if (activityTimeout) {
            inactive.current.clear()
            inactive.current = debounce(() => {
                inactivityRef.current = true
                // console.log("Inactivity: TRUE.")
            }, activityTimeout)
        }
    }, [activityTimeout])

    const token: string | undefined = tokenState?.tag === 'valid' ? tokenState.token : undefined

    useEffect(() => {
        if (token) {
            // console.log("Polling started.")
            poll(validate, 1000)
        }
    }, [token])

    return <></>
}

export const SessionTimeLeft = () => {
    const { tokenState } = useAppSelector(selectUser)

    if (tokenState === undefined || tokenState.tag !== 'valid') return null

    if (tokenState.timeLeft && tokenState.timeLeft > 120) return null

    return <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: .5,
        width: '90px',
        fontSize: '.8rem',
    }}>
        <AccessTimeIcon sx={{ width: '1rem', height: '1rem' }} />
        <span>{showTime(tokenState.timeLeft)}</span>
    </Box>
}

function showTime(t: number | undefined): string {
    if (t === undefined) return '...'

    if (t < 60) return `> 1 min`

    const mins = Math.ceil(t / 60)

    return `> ${mins} min`
}
